import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import localStorageConstants from "src/app/constants/localStorage.constants";
import {
  ConnectedRelative,
  EventInvitation,
  EventJoinRequest,
  EventSummary,
} from "src/app/models/screens/events.model";
import { FilesStorageRequest } from "src/app/models/screens/files.model";
import {
  Payment,
  UpcomingPayment,
} from "src/app/models/screens/payments.model";
import {
  Task,
  TaskRequest,
  TaskRequestStatus,
  TaskStatus,
} from "src/app/models/screens/task.model";
import {
  acceptTaskRequest,
  decideFilesStorageRequest,
  getAllPayments,
  getConnectedRelatives,
  getFilesStorageRequests,
  getLogs,
  getMyEvents,
  getParentChildren,
  getReceivedEventInvitations,
  getReceivedEventJoinRequests,
  getReceivedTaskRequests,
  getTasksForDate,
  getUpcomingPayments,
  rejectTaskRequest,
  respondToEventInvitation,
  respondToEventJoinRequest,
  respondToMoreTimeRequest,
} from "src/app/utils/backendEndpointCalls.util";

interface ParentChild {
  _id: string;
  firstName: string;
}

interface SelectorUser {
  id: string;
  label: string;
}

export interface PodDashboardLogEntry {
  id: string;
  type: string;
  createdAt: string;
  text: string;
}

const PodDashboardScreenViewModel = () => {
  const [role, setRole] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isFirstTimeVisiting, setIsFirstTimeVisiting] =
    useState<boolean>(false);

  const [selectorUsers, setSelectorUsers] = useState<SelectorUser[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedDashboardOwnerId, setSelectedDashboardOwnerId] =
    useState<string>("you");

  const [todaysTasks, setTodaysTasks] = useState<Task[]>([]);
  const [isLoadingTodaysTasks, setIsLoadingTodaysTasks] =
    useState<boolean>(false);

  const [pendingTaskRequests, setPendingTaskRequests] = useState<TaskRequest[]>(
    [],
  );
  const [isLoadingTaskRequests, setIsLoadingTaskRequests] =
    useState<boolean>(false);

  const [upcomingEvents, setUpcomingEvents] = useState<EventSummary[]>([]);
  const [isLoadingUpcomingEvents, setIsLoadingUpcomingEvents] =
    useState<boolean>(false);

  const [logs, setLogs] = useState<PodDashboardLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false);

  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
  const [isLoadingRecentPayments, setIsLoadingRecentPayments] =
    useState<boolean>(false);

  const [pendingJoinRequests, setPendingJoinRequests] = useState<
    EventJoinRequest[]
  >([]);
  const [isLoadingJoinRequests, setIsLoadingJoinRequests] =
    useState<boolean>(false);
  const [respondingJoinRequestId, setRespondingJoinRequestId] = useState<
    string | null
  >(null);

  const [pendingInvitations, setPendingInvitations] = useState<
    EventInvitation[]
  >([]);
  const [isLoadingInvitations, setIsLoadingInvitations] =
    useState<boolean>(false);
  const [respondingInvitationId, setRespondingInvitationId] = useState<
    string | null
  >(null);

  type CompanionPrompt = {
    invitationId: string;
    eventTitle: string;
    requireAtLeastOne: boolean;
    relativeLabel: string;
  };
  const [companionPrompt, setCompanionPrompt] =
    useState<CompanionPrompt | null>(null);
  const [companionRelatives, setCompanionRelatives] = useState<
    ConnectedRelative[]
  >([]);
  const [isLoadingCompanionRelatives, setIsLoadingCompanionRelatives] =
    useState<boolean>(false);
  const [selectedCompanionIds, setSelectedCompanionIds] = useState<string[]>(
    [],
  );
  const [isSubmittingCompanionPrompt, setIsSubmittingCompanionPrompt] =
    useState<boolean>(false);

  const [scheduledPayments, setScheduledPayments] = useState<UpcomingPayment[]>(
    [],
  );
  const [isLoadingScheduledPayments, setIsLoadingScheduledPayments] =
    useState<boolean>(false);

  const [pendingStorageRequests, setPendingStorageRequests] = useState<
    FilesStorageRequest[]
  >([]);
  const [isLoadingStorageRequests, setIsLoadingStorageRequests] =
    useState<boolean>(false);
  const [decidingStorageRequestId, setDecidingStorageRequestId] = useState<
    string | null
  >(null);

  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);

  const [now, setNow] = useState<Dayjs>(dayjs());

  const showError = (message: string) => {
    setIsSnackbarError(true);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const showSuccess = (message: string) => {
    setIsSnackbarError(false);
    setSnackbarMessage(message);
    setIsDisplayingSnackbar(true);
  };

  const getCurrentChildId = useCallback((): string | undefined => {
    if (role !== "Parent") return undefined;
    return selectedDashboardOwnerId === "you"
      ? undefined
      : selectedDashboardOwnerId;
  }, [role, selectedDashboardOwnerId]);

  const getLogsUserId = useCallback((): string | undefined => {
    if (role === "Parent") {
      return selectedDashboardOwnerId === "you"
        ? currentUserId || undefined
        : selectedDashboardOwnerId;
    }
    return currentUserId || undefined;
  }, [currentUserId, role, selectedDashboardOwnerId]);

  const minutesToLabel = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const formatCurrency = (amount: number, currency: string): string => {
    if (currency === "GBP") return `£${amount.toFixed(2)}`;
    return `${amount.toFixed(2)} ${currency}`;
  };

  const formatDateTime = (value: string): string => {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;
    return parsedDate.toLocaleString();
  };

  const getTaskStatusChipColor = (
    status: TaskStatus,
  ): "warning" | "success" | "error" | "default" => {
    if (status === "Completed") return "success";
    if (status === "CompletedOverdue") return "warning";
    if (status === "Incomplete") return "error";
    return "default";
  };

  const getRequestStatusChipColor = (
    status: TaskRequestStatus,
  ): "warning" | "success" | "error" => {
    if (status === "Accepted") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };

  const getStorageRequestStatusChipColor = (
    status: string,
  ): "warning" | "success" | "error" => {
    if (status === "Accepted") return "success";
    if (status === "Rejected") return "error";
    return "warning";
  };

  const getLogTypeChipColor = (
    type: string,
  ): "default" | "primary" | "secondary" | "success" | "warning" | "error" => {
    const normalizedType = type.trim().toLowerCase();
    if (
      normalizedType.includes("error") ||
      normalizedType.includes("fail") ||
      normalizedType.includes("reject")
    )
      return "error";
    if (
      normalizedType.includes("warning") ||
      normalizedType.includes("suspend")
    )
      return "warning";
    if (
      normalizedType.includes("success") ||
      normalizedType.includes("accept") ||
      normalizedType.includes("created")
    )
      return "success";
    if (normalizedType.includes("update") || normalizedType.includes("edit"))
      return "primary";
    if (normalizedType.includes("link") || normalizedType.includes("relation"))
      return "secondary";
    return "default";
  };

  const loadParentChildren = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const fetchedChildren: ParentChild[] = response.children ?? [];
    setSelectorUsers(
      fetchedChildren.map((child: ParentChild) => ({
        id: child._id,
        label: child.firstName,
      })),
    );
  }, []);

  const loadTodaysTasks = useCallback(
    async (date: string, childId?: string) => {
      setIsLoadingTodaysTasks(true);
      const response = await getTasksForDate(date, childId);
      setIsLoadingTodaysTasks(false);
      if (!response.success) {
        showError(response.error);
        return;
      }
      const sorted = [...(response.tasks ?? [])].sort(
        (a: Task, b: Task) => a.from - b.from,
      );
      setTodaysTasks(sorted);
    },
    [],
  );

  const loadPendingTaskRequests = useCallback(async (childId?: string) => {
    setIsLoadingTaskRequests(true);
    const response = await getReceivedTaskRequests(childId);
    setIsLoadingTaskRequests(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingTaskRequests(
      (response.requests ?? []).filter(
        (req: TaskRequest) => req.status === "Pending",
      ),
    );
  }, []);

  const loadUpcomingEvents = useCallback(async (childId?: string) => {
    setIsLoadingUpcomingEvents(true);
    const response = await getMyEvents(childId);
    setIsLoadingUpcomingEvents(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const today = dayjs().format("YYYY-MM-DD");
    const sevenDaysOut = dayjs().add(7, "day").format("YYYY-MM-DD");
    const filtered = (response.events ?? []).filter((evt: EventSummary) => {
      return evt.date >= today && evt.date <= sevenDaysOut;
    });
    setUpcomingEvents(filtered);
  }, []);

  const loadLogs = useCallback(async (userId?: string) => {
    setIsLoadingLogs(true);
    const response = await getLogs(1, "pod", userId);
    setIsLoadingLogs(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setLogs((response.logs ?? []).slice(0, 10));
  }, []);

  const loadRecentPayments = useCallback(async (childId?: string) => {
    setIsLoadingRecentPayments(true);
    const response = await getAllPayments(childId);
    setIsLoadingRecentPayments(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const sevenDaysAgo = dayjs().subtract(7, "day").format("YYYY-MM-DD");
    const recent = (response.receivedPayments ?? [])
      .filter((p: Payment) => p.date >= sevenDaysAgo)
      .sort((a: Payment, b: Payment) => (a.date < b.date ? 1 : -1));
    setRecentPayments(recent);
  }, []);

  const loadPendingJoinRequests = useCallback(async (childId?: string) => {
    setIsLoadingJoinRequests(true);
    const response = await getReceivedEventJoinRequests(childId);
    setIsLoadingJoinRequests(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingJoinRequests(
      (response.joinRequests ?? []).filter(
        (req: EventJoinRequest) => req.status === "Pending",
      ),
    );
  }, []);

  const loadPendingInvitations = useCallback(async (childId?: string) => {
    setIsLoadingInvitations(true);
    const response = await getReceivedEventInvitations(childId);
    setIsLoadingInvitations(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingInvitations(
      (response.invitations ?? []).filter(
        (inv: EventInvitation) => inv.status === "Pending",
      ),
    );
  }, []);

  const loadScheduledPayments = useCallback(async () => {
    setIsLoadingScheduledPayments(true);
    const response = await getUpcomingPayments();
    setIsLoadingScheduledPayments(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    const filtered = (response.upcomingPayments ?? [])
      .filter(
        (p: UpcomingPayment) => p.daysUntilDue >= 0 && p.daysUntilDue <= 7,
      )
      .sort(
        (a: UpcomingPayment, b: UpcomingPayment) =>
          a.daysUntilDue - b.daysUntilDue,
      );
    setScheduledPayments(filtered);
  }, []);

  const loadPendingStorageRequests = useCallback(async () => {
    setIsLoadingStorageRequests(true);
    const response = await getFilesStorageRequests();
    setIsLoadingStorageRequests(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingStorageRequests(
      (response.requests ?? []).filter(
        (req: FilesStorageRequest) => req.status === "Pending",
      ),
    );
  }, []);

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);

    setIsFirstTimeVisiting(
      localStorage.getItem(localStorageConstants.FIRST_TIME_VISITING) ===
        "true",
    );
    localStorage.setItem(localStorageConstants.FIRST_TIME_VISITING, "false");

    const storedUserId =
      localStorage.getItem((localStorageConstants as any).ID) ??
      localStorage.getItem("ID") ??
      "";
    setCurrentUserId(storedUserId);
  }, []);

  useEffect(() => {
    if (role !== "Parent") {
      setSelectorUsers([]);
      return;
    }
    loadParentChildren();
  }, [loadParentChildren, role]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!role) return;

    const todayStr = dayjs().format("YYYY-MM-DD");
    const childId = getCurrentChildId();

    loadTodaysTasks(todayStr, childId);
    loadUpcomingEvents(childId);
    loadLogs(getLogsUserId());

    if (role !== "Guest") {
      loadPendingTaskRequests(childId);
      loadRecentPayments(childId);
      loadPendingJoinRequests(childId);
      loadPendingInvitations(childId);
    }

    if (role === "Admin") {
      loadScheduledPayments();
      loadPendingStorageRequests();
    }
  }, [
    getCurrentChildId,
    getLogsUserId,
    loadLogs,
    loadPendingInvitations,
    loadPendingJoinRequests,
    loadPendingStorageRequests,
    loadPendingTaskRequests,
    loadRecentPayments,
    loadScheduledPayments,
    loadTodaysTasks,
    loadUpcomingEvents,
    role,
    selectedDashboardOwnerId,
  ]);

  useEffect(() => {
    if (!role) return;
    const todayStr = dayjs().format("YYYY-MM-DD");
    if (now.format("YYYY-MM-DD") !== todayStr) return;
    loadTodaysTasks(todayStr, getCurrentChildId());
  }, [getCurrentChildId, loadTodaysTasks, now, role]);

  useEffect(() => {
    const isAlreadySorted = todaysTasks.every((task, index, arr) => {
      if (index === 0) return true;
      return arr[index - 1].from <= task.from;
    });
    if (!isAlreadySorted) {
      setTodaysTasks((prev) => [...prev].sort((a, b) => a.from - b.from));
    }
  }, [todaysTasks]);

  useEffect(() => {
    const compareEvents = (a: EventSummary, b: EventSummary) => {
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      return a.from - b.from;
    };
    const isAlreadySorted = upcomingEvents.every((evt, index, arr) => {
      if (index === 0) return true;
      return compareEvents(arr[index - 1], evt) <= 0;
    });
    if (!isAlreadySorted) {
      setUpcomingEvents((prev) => [...prev].sort(compareEvents));
    }
  }, [upcomingEvents]);

  const onSelectedDashboardOwnerChanged = (ownerId: string) => {
    setSelectedDashboardOwnerId(ownerId);
    setTodaysTasks([]);
    setPendingTaskRequests([]);
    setUpcomingEvents([]);
    setRecentPayments([]);
    setPendingJoinRequests([]);
    setPendingInvitations([]);
    setLogs([]);
  };

  const onAcceptTaskRequest = async (requestId: string) => {
    const response = await acceptTaskRequest(requestId, getCurrentChildId());
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingTaskRequests((prev) =>
      prev.filter((req) => req.id !== requestId),
    );
    await loadTodaysTasks(dayjs().format("YYYY-MM-DD"), getCurrentChildId());
    await loadLogs(getLogsUserId());
    showSuccess("Task request accepted.");
  };

  const onRejectTaskRequest = async (requestId: string) => {
    const response = await rejectTaskRequest(requestId, getCurrentChildId());
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingTaskRequests((prev) =>
      prev.filter((req) => req.id !== requestId),
    );
    await loadLogs(getLogsUserId());
    showSuccess("Task request rejected.");
  };

  const onAcceptMoreTimeRequest = async (requestId: string) => {
    const response = await respondToMoreTimeRequest(
      requestId,
      true,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingTaskRequests((prev) =>
      prev.map((req) =>
        req.id === requestId ? { ...req, moreTimeStatus: "Accepted" } : req,
      ),
    );
    await loadLogs(getLogsUserId());
    showSuccess("Reschedule request accepted.");
  };

  const onRejectMoreTimeRequest = async (requestId: string) => {
    const response = await respondToMoreTimeRequest(
      requestId,
      false,
      getCurrentChildId(),
    );
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingTaskRequests((prev) =>
      prev.map((req) =>
        req.id === requestId ? { ...req, moreTimeStatus: "Rejected" } : req,
      ),
    );
    await loadLogs(getLogsUserId());
    showSuccess("Reschedule request rejected.");
  };

  const actingUserIsModel = useMemo(() => {
    if (role === "Model") return true;
    if (role === "Parent" && selectedDashboardOwnerId !== "you") return true;
    return false;
  }, [role, selectedDashboardOwnerId]);

  const actingUserIsParent = useMemo(() => {
    if (role !== "Parent") return false;
    return selectedDashboardOwnerId === "you";
  }, [role, selectedDashboardOwnerId]);

  const shouldPromptForCompanions = actingUserIsModel || actingUserIsParent;

  const closeCompanionPrompt = () => {
    setCompanionPrompt(null);
    setCompanionRelatives([]);
    setSelectedCompanionIds([]);
  };

  const toggleCompanionSelection = (relativeId: string) => {
    setSelectedCompanionIds((prev) =>
      prev.includes(relativeId)
        ? prev.filter((id) => id !== relativeId)
        : [...prev, relativeId],
    );
  };

  const openCompanionPrompt = async (
    invitation: EventInvitation,
  ): Promise<void> => {
    setCompanionPrompt({
      invitationId: invitation.id,
      eventTitle: invitation.event.title,
      requireAtLeastOne: actingUserIsModel,
      relativeLabel: actingUserIsModel
        ? "Connected parents"
        : "Connected children",
    });
    setSelectedCompanionIds([]);
    setIsLoadingCompanionRelatives(true);
    const response = await getConnectedRelatives(getCurrentChildId());
    setIsLoadingCompanionRelatives(false);
    if (!response.success) {
      showError(response.error);
      setCompanionPrompt(null);
      return;
    }
    setCompanionRelatives(response.relatives ?? []);
  };

  const submitCompanionPrompt = async () => {
    if (!companionPrompt) return;
    if (
      companionPrompt.requireAtLeastOne &&
      selectedCompanionIds.length === 0
    ) {
      showError(
        `Please select at least one ${companionPrompt.relativeLabel.toLowerCase()}.`,
      );
      return;
    }
    const invitationId = companionPrompt.invitationId;
    setIsSubmittingCompanionPrompt(true);
    setRespondingInvitationId(invitationId);
    const response = await respondToEventInvitation(
      invitationId,
      true,
      selectedCompanionIds,
      getCurrentChildId(),
    );
    setRespondingInvitationId(null);
    setIsSubmittingCompanionPrompt(false);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingInvitations((prev) =>
      prev.filter((inv) => inv.id !== invitationId),
    );
    await loadUpcomingEvents(getCurrentChildId());
    await loadLogs(getLogsUserId());
    showSuccess("Invitation accepted.");
    closeCompanionPrompt();
  };

  const onRespondInvitation = async (invitationId: string, accept: boolean) => {
    if (!accept) {
      setRespondingInvitationId(invitationId);
      const response = await respondToEventInvitation(
        invitationId,
        false,
        undefined,
        getCurrentChildId(),
      );
      setRespondingInvitationId(null);
      if (!response.success) {
        showError(response.error);
        return;
      }
      setPendingInvitations((prev) =>
        prev.filter((inv) => inv.id !== invitationId),
      );
      await loadLogs(getLogsUserId());
      showSuccess("Invitation rejected.");
      return;
    }

    if (shouldPromptForCompanions) {
      const invitation = pendingInvitations.find(
        (inv) => inv.id === invitationId,
      );
      if (!invitation) {
        showError("Invitation not found.");
        return;
      }
      await openCompanionPrompt(invitation);
      return;
    }

    setRespondingInvitationId(invitationId);
    const response = await respondToEventInvitation(
      invitationId,
      true,
      undefined,
      getCurrentChildId(),
    );
    setRespondingInvitationId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingInvitations((prev) =>
      prev.filter((inv) => inv.id !== invitationId),
    );
    await loadUpcomingEvents(getCurrentChildId());
    await loadLogs(getLogsUserId());
    showSuccess("Invitation accepted.");
  };

  const onRespondJoinRequest = async (
    joinRequestId: string,
    accept: boolean,
  ) => {
    setRespondingJoinRequestId(joinRequestId);
    const response = await respondToEventJoinRequest(
      joinRequestId,
      accept,
      getCurrentChildId(),
    );
    setRespondingJoinRequestId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingJoinRequests((prev) =>
      prev.filter((req) => req.id !== joinRequestId),
    );
    await loadLogs(getLogsUserId());
    showSuccess(accept ? "Join request accepted." : "Join request rejected.");
  };

  const onDecideStorageRequest = async (requestId: string, accept: boolean) => {
    setDecidingStorageRequestId(requestId);
    const response = await decideFilesStorageRequest(requestId, accept);
    setDecidingStorageRequestId(null);
    if (!response.success) {
      showError(response.error);
      return;
    }
    setPendingStorageRequests((prev) =>
      prev.filter((req) => req.id !== requestId),
    );
    await loadLogs(getLogsUserId());
    showSuccess(
      accept ? "Storage request accepted." : "Storage request rejected.",
    );
  };

  const highlightedTaskId = useMemo(() => {
    if (todaysTasks.length === 0) return null;
    const currentMinutes = now.hour() * 60 + now.minute();
    const collidingTask = todaysTasks.find(
      (task) => currentMinutes >= task.from && currentMinutes < task.to,
    );
    if (collidingTask) return collidingTask.id;
    const nextTask = todaysTasks.find((task) => task.from >= currentMinutes);
    if (nextTask) return nextTask.id;
    return null;
  }, [todaysTasks, now]);

  return {
    role,
    isFirstTimeVisiting,
    setIsFirstTimeVisiting,
    selectorUsers,
    isLoadingChildren,
    selectedDashboardOwnerId,
    onSelectedDashboardOwnerChanged,
    todaysTasks,
    isLoadingTodaysTasks,
    highlightedTaskId,
    pendingTaskRequests,
    isLoadingTaskRequests,
    onAcceptTaskRequest,
    onRejectTaskRequest,
    onAcceptMoreTimeRequest,
    onRejectMoreTimeRequest,
    upcomingEvents,
    isLoadingUpcomingEvents,
    logs,
    isLoadingLogs,
    recentPayments,
    isLoadingRecentPayments,
    pendingJoinRequests,
    isLoadingJoinRequests,
    respondingJoinRequestId,
    onRespondJoinRequest,
    pendingInvitations,
    isLoadingInvitations,
    respondingInvitationId,
    onRespondInvitation,
    companionPrompt,
    companionRelatives,
    isLoadingCompanionRelatives,
    selectedCompanionIds,
    isSubmittingCompanionPrompt,
    toggleCompanionSelection,
    submitCompanionPrompt,
    closeCompanionPrompt,
    scheduledPayments,
    isLoadingScheduledPayments,
    pendingStorageRequests,
    isLoadingStorageRequests,
    decidingStorageRequestId,
    onDecideStorageRequest,
    minutesToLabel,
    formatCurrency,
    formatDateTime,
    getTaskStatusChipColor,
    getRequestStatusChipColor,
    getStorageRequestStatusChipColor,
    getLogTypeChipColor,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
  };
};

export default PodDashboardScreenViewModel;
